/**
 * GPU-SKINNED CROWD.
 *
 * What this replaces, and why it had to be replaced:
 *
 * The horde was a FLIPBOOK. `bakePose()` froze a skinned mesh at one
 * instant into static geometry -- throwing the skeleton away -- and the
 * draw loop cycled FOUR such stills at seven frames a second. Four frames,
 * no interpolation, no per-body state, no transitions. That is sprite
 * animation performed with 3D models, and it is the single reason the
 * game read as an arcade cabinet rather than as something made this
 * decade. No amount of per-instance matrix nudging fixes it, because the
 * thing being nudged is a photograph.
 *
 * The modern answer is to keep the skeleton and do the skinning on the
 * GPU: bake every clip's BONE MATRICES into a float texture, keep the
 * mesh's skin indices and weights, and let the vertex shader look up the
 * pose per instance. One draw call, hundreds of bodies, continuous motion,
 * per-body clip choice and real crossfades between clips.
 *
 * Bone matrices rather than vertex positions is the load-bearing choice.
 * A vertex-animation texture stores every vertex of every frame: at 1,651
 * verts x 270 frames that is 1.8MB of texture per tier before mipmaps. A
 * bone texture stores 65 joints instead of 1,651 vertices -- about 25x
 * smaller -- and it is what keeps a real crowd inside a 10MB budget.
 *
 * Zero sim coupling. This module owns pixels; it reads no run state and
 * writes none.
 */
import {
  BufferGeometry, DataTexture, FloatType, InstancedBufferAttribute,
  InstancedMesh, Matrix4, MeshStandardMaterial, NearestFilter, Object3D,
  RGBAFormat, SkinnedMesh, AnimationMixer, AnimationClip, MeshDepthMaterial,
  RGBADepthPacking, Color, type Material,
} from 'three';

/**
 * Sampling rate for the bone texture, in frames per second.
 *
 * Mixamo authors at 30fps, so 30 samples the source exactly and any
 * higher rate only interpolates data that was never there. The shader
 * therefore does NOT interpolate within a clip -- it snaps to the sampled
 * frame, which is the same thing the source animation does.
 */
const BAKE_FPS = 30;

/** Default instance tint — no multiply. */
const WHITE = new Color(1, 1, 1);

/** Where one clip lives in the texture, in rows. */
export interface ClipRange {
  name: string;
  /** First row of this clip. */
  row: number;
  /** How many rows (frames) it occupies. */
  frames: number;
  /** Clip length in seconds. */
  duration: number;
}

export interface CrowdRig {
  mesh: InstancedMesh;
  clips: Map<string, ClipRange>;
  /** Longest clip name list, for debugging what actually got baked. */
  readonly clipNames: string[];
  /**
   * Absolute texture row for a clip at time `t` seconds.
   * @param loop when false, the clip HOLDS on its last frame instead of
   *   wrapping — a death that loops is a body that stands back up.
   */
  row(clip: string, t: number, loop?: boolean): number;
  duration(clip: string): number;
  /** Reset the write cursor for this frame. */
  begin(): void;
  /**
   * Append one body. `rowA`/`rowB` are absolute texture rows and `blend`
   * crossfades between them, which is how a body moves from walking to
   * being hit without the pose popping.
   */
  push(m: Matrix4, rowA: number, rowB: number, blend: number, tint?: Color): void;
  /** Upload this frame's instances. */
  end(): void;
  readonly drawn: number;
  /** Half-width as a fraction of body height, from the POSED skeleton. */
  readonly halfW: number;
  /** Body height in the rig's own units, for world-scale fitting. */
  readonly bodyH: number;
}

/**
 * Find the largest skinned mesh in a loaded scene.
 *
 * Largest, not first: these exports carry props (a weapon, a torch) as
 * separate meshes, and picking whichever the traversal reached first is
 * how a crowd ends up as a field of floating pistols.
 */
function largestSkinned(scene: Object3D): SkinnedMesh | null {
  let best: SkinnedMesh | null = null;
  let bestVerts = -1;
  scene.traverse((o) => {
    const m = o as SkinnedMesh;
    if (!m.isSkinnedMesh) return;
    const n = m.geometry?.getAttribute('position')?.count ?? 0;
    if (n > bestVerts) { bestVerts = n; best = m; }
  });
  return best;
}

/**
 * Bake every clip's bone matrices into one float texture.
 *
 * Layout: one ROW per animation frame; each joint occupies four
 * consecutive texels holding the four COLUMNS of its 4x4 matrix, which is
 * the order `mat4(...)` consumes them in GLSL.
 */
function bakeBoneTexture(
  src: SkinnedMesh, clips: AnimationClip[], root: Object3D,
): { tex: DataTexture; ranges: Map<string, ClipRange>; width: number;
     height: number; halfW: number; bodyH: number } {
  const bones = src.skeleton.bones.length;
  const width = bones * 4;

  const ranges = new Map<string, ClipRange>();
  let rows = 0;
  for (const c of clips) {
    const frames = Math.max(2, Math.round(c.duration * BAKE_FPS));
    ranges.set(c.name, { name: c.name, row: rows, frames, duration: c.duration });
    rows += frames;
  }

  const data = new Float32Array(width * rows * 4);
  const mixer = new AnimationMixer(root);

  /**
   * The body's real silhouette, measured from the POSED SKELETON rather
   * than the bind-pose bounding box.
   *
   * The bind pose is a T- or A-pose: arms straight out. Measuring it gave
   * a half-width of 0.53 of body height, which pushed every body almost a
   * metre inboard of the kerb to satisfy the rail law — a horde visibly
   * refusing to walk near the rails. A walking body is roughly a fifth of
   * its height across the shoulders, and THAT is the number the rail law
   * should use, because it is the width actually on screen.
   */
  let maxAbsX = 0, minY = Infinity, maxY = -Infinity;

  for (const c of clips) {
    const range = ranges.get(c.name)!;
    const action = mixer.clipAction(c);
    mixer.stopAllAction();
    action.reset();
    action.play();
    for (let f = 0; f < range.frames; f++) {
      // Sample across the clip's own duration. The last frame lands one
      // step BEFORE the end for a looping clip, so walk cycles do not
      // stutter by repeating their first pose twice.
      mixer.setTime((f / range.frames) * c.duration);
      root.updateMatrixWorld(true);
      src.skeleton.update();
      // boneMatrices is allocated by Skeleton.init(); a null here means
      // the rig never bound, which would silently bake a texture of zeros.
      const bm = src.skeleton.boneMatrices;
      if (!bm) throw new Error('crowd: skeleton has no boneMatrices — rig did not bind');
      data.set(bm, (range.row + f) * width * 4);
      // Bone world positions, this frame. `boneMatrices` holds
      // bone.matrixWorld * boneInverse, so its translation is NOT the
      // bone's position — read the bones themselves.
      for (const bone of src.skeleton.bones) {
        const e = bone.matrixWorld.elements;
        const ax = Math.abs(e[12]!);
        if (ax > maxAbsX) maxAbsX = ax;
        if (e[13]! < minY) minY = e[13]!;
        if (e[13]! > maxY) maxY = e[13]!;
      }
    }
    action.stop();
  }

  const tex = new DataTexture(data, width, rows, RGBAFormat, FloatType);
  tex.minFilter = NearestFilter;
  tex.magFilter = NearestFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  // The skeleton stops at the joint; flesh, kit and hands sit outside it,
  // so the measured extent gets a margin. A body must never be rendered
  // NARROWER than it is — the rail law depends on this being an upper
  // bound on what the eye can see crossing the kerb.
  const geoH = maxY > minY ? maxY - minY : 1;
  const halfW = (maxAbsX / geoH) * 1.25;
  return { tex, ranges, width, height: rows, halfW, bodyH: geoH };
}

/**
 * Inject the skinning into a stock three material.
 *
 * onBeforeCompile rather than a hand-written ShaderMaterial on purpose:
 * it keeps three's lighting, fog, tone mapping, shadow reception and
 * instancing intact. A bespoke shader would have to reimplement all of
 * them, and the ones it got wrong would be exactly the ones that make a
 * frame read as flat.
 */
function injectSkinning(
  mat: Material, tex: DataTexture, width: number, height: number,
  bindMatrix: Matrix4, bindMatrixInverse: Matrix4,
): void {
  mat.onBeforeCompile = (shader) => {
    shader.uniforms['uBoneTex'] = { value: tex };
    shader.uniforms['uBoneTexSize'] = { value: [width, height] };
    shader.uniforms['uBindMatrix'] = { value: bindMatrix };
    shader.uniforms['uBindMatrixInverse'] = { value: bindMatrixInverse };

    shader.vertexShader = `
      uniform sampler2D uBoneTex;
      uniform vec2 uBoneTexSize;
      uniform mat4 uBindMatrix;
      uniform mat4 uBindMatrixInverse;
      attribute vec3 aAnim;      // x = row A, y = row B, z = crossfade
      // three declares these ONLY under USE_SKINNING, which it sets for a
      // SkinnedMesh. This is an InstancedMesh carrying skinned geometry,
      // so the attributes are present in the buffer but undeclared in the
      // shader unless we declare them ourselves.
      attribute vec4 skinIndex;
      attribute vec4 skinWeight;

      // GLSL's mix() has no matrix overload — it is defined for genType
      // only. Blending two poses means blending the four columns.
      mat4 crowdMix(mat4 a, mat4 b, float t) {
        return mat4(mix(a[0], b[0], t), mix(a[1], b[1], t),
                    mix(a[2], b[2], t), mix(a[3], b[3], t));
      }

      mat4 crowdBone(float row, float bone) {
        float dx = 1.0 / uBoneTexSize.x;
        float x = (bone * 4.0 + 0.5) * dx;
        float y = (row + 0.5) / uBoneTexSize.y;
        return mat4(
          texture2D(uBoneTex, vec2(x,            y)),
          texture2D(uBoneTex, vec2(x + dx,       y)),
          texture2D(uBoneTex, vec2(x + dx * 2.0, y)),
          texture2D(uBoneTex, vec2(x + dx * 3.0, y))
        );
      }

      // ONE blended skinning matrix per vertex, reused for position and
      // normal. Building it once is what keeps this affordable: the naive
      // form fetches the bone texture twice as often for no visible gain.
      mat4 crowdMatrix() {
        mat4 m = mat4(0.0);
        m += skinWeight.x * crowdMix(crowdBone(aAnim.x, skinIndex.x), crowdBone(aAnim.y, skinIndex.x), aAnim.z);
        m += skinWeight.y * crowdMix(crowdBone(aAnim.x, skinIndex.y), crowdBone(aAnim.y, skinIndex.y), aAnim.z);
        m += skinWeight.z * crowdMix(crowdBone(aAnim.x, skinIndex.z), crowdBone(aAnim.y, skinIndex.z), aAnim.z);
        m += skinWeight.w * crowdMix(crowdBone(aAnim.x, skinIndex.w), crowdBone(aAnim.y, skinIndex.w), aAnim.z);
        return m;
      }
    ` + shader.vertexShader;

    shader.vertexShader = shader.vertexShader
      // Declare the pose at the TOP OF MAIN, not inside one of the chunk
      // injections. MeshStandardMaterial includes <beginnormal_vertex>;
      // MeshDepthMaterial — which draws the shadow pass — does not, so
      // declaring it there left the shadow shader referring to a matrix
      // that did not exist and the whole crowd cast no shadow at all.
      .replace('void main() {', `
        void main() {
        mat4 crowdSkin = crowdMatrix();
      `)
      .replace('#include <beginnormal_vertex>', `
        #include <beginnormal_vertex>
        objectNormal = mat3(uBindMatrixInverse) * (mat3(crowdSkin) * (mat3(uBindMatrix) * objectNormal));
      `)
      .replace('#include <begin_vertex>', `
        #include <begin_vertex>
        transformed = (uBindMatrixInverse * (crowdSkin * (uBindMatrix * vec4(transformed, 1.0)))).xyz;
      `);
  };
  // Force a recompile and give the material its own program cache slot,
  // so a stock clone of this material elsewhere is not handed our shader.
  mat.customProgramCacheKey = () => 'bonwarsy-crowd';
  mat.needsUpdate = true;
}

/**
 * Build a GPU-skinned crowd from a loaded rig.
 *
 * @param gltfScene the loaded GLB scene (must contain a skinned mesh)
 * @param clips     its animation clips
 * @param capacity  maximum simultaneous bodies
 */
export function createCrowd(
  gltfScene: Object3D, clips: AnimationClip[], capacity: number,
): CrowdRig | null {
  // Bake from a scene at IDENTITY: bone matrices are captured in the
  // rig's own space, so any transform left on the root would be baked
  // into every pose and then applied a second time by the instance
  // matrix.
  gltfScene.position.set(0, 0, 0);
  gltfScene.quaternion.identity();
  gltfScene.scale.set(1, 1, 1);
  gltfScene.updateMatrixWorld(true);

  const src = largestSkinned(gltfScene);
  if (!src || clips.length === 0) return null;

  const { tex, ranges, width, height, halfW, bodyH } = bakeBoneTexture(src, clips, gltfScene);

  const geo = src.geometry as BufferGeometry;
  const baseMat = (Array.isArray(src.material) ? src.material[0] : src.material) as MeshStandardMaterial;
  const mat = baseMat.clone();
  mat.roughness = 0.85;
  mat.metalness = 0.0;

  const bindMatrix = src.bindMatrix.clone();
  const bindMatrixInverse = src.bindMatrixInverse.clone();
  injectSkinning(mat, tex, width, height, bindMatrix, bindMatrixInverse);

  const mesh = new InstancedMesh(geo, mat, capacity);
  mesh.frustumCulled = false;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.count = 0;
  // Allocate the colour buffer up front. setColorAt on an InstancedMesh
  // with no instanceColor allocates lazily and fills the REST of the
  // buffer with black, so the first frame paints most of the crowd as
  // silhouettes until every slot has been written at least once.
  mesh.instanceColor = new InstancedBufferAttribute(
    new Float32Array(capacity * 3).fill(1), 3);
  mesh.instanceColor.setUsage(35048 /* DynamicDrawUsage */);

  const anim = new InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
  anim.setUsage(35048 /* DynamicDrawUsage */);
  geo.setAttribute('aAnim', anim);

  // THE SHADOW MUST SEE THE SAME POSE. Without its own depth material the
  // shadow pass runs the stock vertex shader, so every body casts the
  // shadow of its BIND pose -- a T-shaped shadow under a walking body,
  // which is worse than no shadow at all.
  const depth = new MeshDepthMaterial({ depthPacking: RGBADepthPacking });
  injectSkinning(depth, tex, width, height, bindMatrix, bindMatrixInverse);
  depth.customProgramCacheKey = () => 'bonwarsy-crowd-depth';
  mesh.customDepthMaterial = depth;

  let cursor = 0;
  const rig: CrowdRig = {
    mesh,
    clips: ranges,
    clipNames: [...ranges.keys()],
    row(clip, t, loop = true) {
      const r = ranges.get(clip);
      if (!r) return 0;
      const f = t * BAKE_FPS;
      const frame = loop
        ? ((f % r.frames) + r.frames) % r.frames
        : Math.min(r.frames - 1, Math.max(0, f));
      return r.row + Math.floor(frame);
    },
    duration(clip) { return ranges.get(clip)?.duration ?? 0; },
    begin() { cursor = 0; },
    push(m, rowA, rowB, blend, tint) {
      if (cursor >= capacity) return;
      mesh.setMatrixAt(cursor, m);
      // ONE mesh now wears every tier, so colour is what still separates a
      // grunt from a brute at a glance. Instance colour is free — three
      // multiplies it into the diffuse in the fragment shader.
      mesh.setColorAt(cursor, tint ?? WHITE);
      const a = anim.array as Float32Array;
      a[cursor * 3] = rowA;
      a[cursor * 3 + 1] = rowB;
      a[cursor * 3 + 2] = blend;
      cursor++;
    },
    end() {
      mesh.count = cursor;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      anim.needsUpdate = true;
    },
    get drawn() { return cursor; },
    halfW,
    bodyH,
  };
  return rig;
}
